import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from "environments/environment";
import { MessagesService } from "../../services/messages.service";
import { Router } from '@angular/router';
import { UserService } from '../../services/user.service';

const BASE_URL = `${environment.baseUrl}/api`;

interface EpaResponse {
  documents_found: string[];
  documents_not_found: string[];
  download_url: string;
}

interface EpaListResponse {
  claims: string[];
}

@Component({
  selector: 'app-epa',
  templateUrl: './epa.component.html',
  styleUrls: ['./epa.component.scss']
})
export class EpaComponent implements OnInit {
  documentId: string = '';
  isDownloading = false;
  isLoadingList = false;
  documentsFound: string[] = [];
  documentsNotFound: string[] = [];
  claimsList: string[] = [];
  foundExpanded = true;
  notFoundExpanded = true;

  constructor(
    private httpClient: HttpClient,
    private messagesService: MessagesService,
    private userService: UserService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    const user = await this.userService.getUser();
    if (!user?.ruoli?.includes('EpaUser')) {
      this.router.navigate(['/unauthorized']);
    }
  }

  toggleFoundSection(): void {
    this.foundExpanded = !this.foundExpanded;
  }

  toggleNotFoundSection(): void {
    this.notFoundExpanded = !this.notFoundExpanded;
  }

  onDocumentIdChange(): void {
    this.claimsList = [];
    this.formatNumDanno();
  }

  private formatNumDanno(): void {
    const id = this.documentId || '';
    const parts = id.split('-');

    let numDannoIdx = -1;
    if (parts.length === 4) {
      // Full format: XXX-XXX-XXXX-XXXXXXXXX
      numDannoIdx = 3;
    } else if (parts.length === 3 && /^\d{4,}$/.test(parts[1])) {
      // Partial format: XXX-XXXX-XXXXXXXXX
      numDannoIdx = 2;
    }

    if (numDannoIdx === -1) return;

    const segment = parts[numDannoIdx];
    if (segment.length === 0 || !/^\d+$/.test(segment)) return;

    const stripped = segment.replace(/^0+/, '') || '0';
    const limited = stripped.length > 9 ? stripped.slice(0, 9) : stripped;
    const padded = limited.padStart(9, '0');

    if (padded !== segment) {
      parts[numDannoIdx] = padded;
      this.documentId = parts.join('-');
    }
  }

  // Full format:    XXX-XXX-XXXX-XXXXXXXX  (with codAgenzia)
  private readonly CLAIM_REGEX_FULL    = /^\d{3}-[A-Za-z0-9]{3}-\d{4}-\d{8,9}$/;
  // Partial format: XXX-XXXX-XXXXXXXX      (without codAgenzia)
  private readonly CLAIM_REGEX_PARTIAL = /^\d{3}-\d{4}-\d{8,9}$/;

  get hasAgencyCode(): boolean {
    return this.CLAIM_REGEX_FULL.test((this.documentId || '').trim());
  }

  get isPartialClaimNumber(): boolean {
    return this.CLAIM_REGEX_PARTIAL.test((this.documentId || '').trim());
  }

  get isActionDisabled(): boolean {
    const id = (this.documentId || '').trim();
    return (
      this.isDownloading ||
      this.isLoadingList ||
      (!this.CLAIM_REGEX_FULL.test(id) && !this.CLAIM_REGEX_PARTIAL.test(id))
    );
  }

  get actionButtonLabel(): string {
    return this.isPartialClaimNumber ? 'Cerca sinistri' : 'Scarica documenti';
  }

  async onAction(): Promise<void> {
    if (this.isPartialClaimNumber) {
      await this.cercaSinistri();
    } else if (this.hasAgencyCode) {
      await this.scaricaDocumenti();
    }
  }

  async cercaSinistri(): Promise<void> {
    this.isLoadingList = true;
    this.claimsList = [];
    this.documentsFound = [];
    this.documentsNotFound = [];
    const url = `${BASE_URL}/epa/list?claimNumber=${this.documentId.trim()}`;

    try {
      const response = await lastValueFrom(this.httpClient.get<EpaListResponse>(url));
      this.claimsList = response.claims ?? [];
      if (this.claimsList.length === 0) {
        this.messagesService.addMessage({ text: 'Nessun sinistro trovato per il codice inserito', type: 'error' });
      }
    } catch (err: any) {
      console.error('List failed', err);
      const status = err instanceof HttpErrorResponse ? err.status : err?.status;
      if (status === 404) {
        this.messagesService.addMessage({ text: 'Nessun sinistro trovato', type: 'error' });
      } else {
        this.messagesService.addMessage({ text: 'Errore durante il recupero della lista sinistri', type: 'error' });
      }
    } finally {
      this.isLoadingList = false;
    }
  }

  async selezionaSinistro(claim: string): Promise<void> {
    this.documentId = claim;
    this.claimsList = [];
    await this.scaricaDocumenti();
  }

  async scaricaDocumenti(): Promise<void> {
    this.isDownloading = true;
    this.documentsFound = [];
    this.documentsNotFound = [];
    this.foundExpanded = true;
    this.notFoundExpanded = true;
    const url = `${BASE_URL}/epa/download?claimNumber=${this.documentId}`;

    try {
      const response = await lastValueFrom(this.httpClient.get<EpaResponse>(url));
      this.documentsFound = response.documents_found ?? [];
      this.documentsNotFound = response.documents_not_found ?? [];
      this.downloadFile(response.download_url, `${this.documentId}.zip`);
    } catch (err: any) {
      console.error('Download failed', err);
      const status = err instanceof HttpErrorResponse ? err.status : err?.status;
      if (status === 404) {
        this.messagesService.addMessage({ text: 'Documenti sinistro non trovati', type: 'error' });
      } else {
        this.messagesService.addMessage({ text: 'Si è verificato un errore durante il download dei documenti', type: 'error' });
      }
    } finally {
      this.isDownloading = false;
    }
  }

  private downloadFile = (downloadUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
}
